import { createContext, type ReactNode, useContext } from 'react';

import {
  useExtractedQuestionsBubble,
  type UseExtractedQuestionsBubbleReturn,
} from '@/hooks/chat/useExtractedQuestionsBubble';

const ExtractedQuestionsBubbleContext = createContext<UseExtractedQuestionsBubbleReturn | null>(
  null
);

interface ProviderProps {
  messageSid: string;
  questionCount: number;
  children: ReactNode;
}

/**
 * Wraps a per-message extracted-questions bubble subtree with the wiring
 * hook's value. Subcomponents read the pulse callbacks via
 * `useExtractedQuestionsBubbleContext()` instead of calling the hook
 * themselves, so one bubble has one set of pulse values.
 */
export function ExtractedQuestionsBubbleProvider({
  messageSid,
  questionCount,
  children,
}: ProviderProps) {
  const value = useExtractedQuestionsBubble({ messageSid, questionCount });

  return (
    <ExtractedQuestionsBubbleContext.Provider value={value}>
      {children}
    </ExtractedQuestionsBubbleContext.Provider>
  );
}

export function useExtractedQuestionsBubbleContext(): UseExtractedQuestionsBubbleReturn {
  const value = useContext(ExtractedQuestionsBubbleContext);

  if (!value) {
    throw new Error(
      'useExtractedQuestionsBubbleContext must be used inside ExtractedQuestionsBubbleProvider'
    );
  }

  return value;
}
